<template>
  <div class="login-page" :class="darkMode ? 'login-page--dark' : 'login-page--light'">
    <div class="hero-grid-overlay"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>

    <div class="login-wrap relative z-10">
      <div class="login-card" :class="darkMode ? 'login-card--dark' : 'login-card--light'">
        <div class="card-stripe"></div>

        <!-- HEADER -->
        <div class="login-header">
          <div class="logo-wrap">
            <img v-if="app.school?.logo" :src="app.school.logo" alt="Logo Școală" class="logo-img" />
            <div v-else class="logo-icon-bg">
              <font-awesome-icon :icon="['fas', 'graduation-cap']" class="logo-icon" />
            </div>
          </div>

          <div class="eyebrow-pill" :class="darkMode ? 'eyebrow-pill--dark' : 'eyebrow-pill--light'">
            <span class="eyebrow-dot"></span>
            <span>Catalog Digital</span>
          </div>

          <h1 class="login-title" :class="darkMode ? 'text-white' : 'text-gray-900'">
            {{ app.school?.name || 'Portal Școlar' }}
          </h1>
          <p class="login-subtitle" :class="darkMode ? 'text-gray-500' : 'text-gray-400'">
            Autentifică-te pentru a accesa catalogul
          </p>
        </div>

        <!-- FORM -->
        <form @submit.prevent="login" class="login-form">
          <div class="form-field">
            <label class="field-label" :class="darkMode ? 'text-gray-400' : 'text-gray-500'">
              <font-awesome-icon :icon="['fas', 'envelope']" class="mr-1" /> Email
            </label>
            <input
              v-model="email"
              type="email"
              placeholder="email@scoala.ro"
              required
              class="input-base"
              :class="darkMode ? 'input-base--dark' : 'input-base--light'"
            />
          </div>

          <div class="form-field">
            <label class="field-label" :class="darkMode ? 'text-gray-400' : 'text-gray-500'">
              <font-awesome-icon :icon="['fas', 'lock']" class="mr-1" /> Parolă
            </label>
            <div class="password-wrap">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="••••••••"
                required
                class="input-base"
                :class="darkMode ? 'input-base--dark' : 'input-base--light'"
              />
              <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
                :class="darkMode ? 'text-gray-500' : 'text-gray-400'"
              >
                <font-awesome-icon :icon="showPassword ? ['fas', 'eye-slash'] : ['fas', 'eye']" />
              </button>
            </div>
          </div>

          <button type="submit" class="btn-submit" :disabled="loading">
            <font-awesome-icon v-if="loading" :icon="['fas', 'spinner']" spin />
            <font-awesome-icon v-else :icon="['fas', 'right-to-bracket']" />
            <span>{{ loading ? 'Se autentifică...' : 'Autentificare' }}</span>
            <svg v-if="!loading" class="btn-arrow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"/>
            </svg>
          </button>
        </form>

        <!-- ROLE BADGES -->
        <div class="role-section" :class="darkMode ? 'role-section--dark' : 'role-section--light'">
          <p class="role-label">Acces pentru</p>
          <div class="role-badges">
            <span v-for="role in roles" :key="role.label" class="role-badge" :class="darkMode ? 'role-badge--dark' : 'role-badge--light'">
              <font-awesome-icon :icon="role.icon" class="text-[10px]" :style="{ color: 'var(--primary-color)' }" />
              {{ role.label }}
            </span>
          </div>
        </div>
<div class="help-text" :class="darkMode ? 'text-gray-400' : 'text-gray-500'">
          Ai uitat parola? Contactează-ne la 
          <a v-if="app.school?.email" :href="`mailto:${app.school.email}`" class="help-link">
            {{ app.school.email }}
          </a>
          <span v-else class="help-link">secretariat</span>.
        </div>
        <!-- FOOTER -->
        <div class="login-footer">
          <button @click="goToSite" class="back-link" :class="darkMode ? 'back-link--dark' : 'back-link--light'">
            <font-awesome-icon :icon="['fas', 'arrow-left']" />
            <span>Înapoi la site</span>
          </button>
          <button @click="toggleDarkMode" class="dark-toggle" :class="darkMode ? 'dark-toggle--dark' : 'dark-toggle--light'">
            <font-awesome-icon :icon="darkMode ? ['fas', 'sun'] : ['fas', 'moon']" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import Swal from 'sweetalert2'
import { useAppStore } from '@/stores/useAppStore'
import { jwtDecode } from 'jwt-decode'

const router = useRouter()
const app = useAppStore()
const darkMode = computed(() => app.darkMode)

const email = ref('')
const password = ref('')
const loading = ref(false)
const showPassword = ref(false)
const goToSite = () => {
  const website = app.school?.website
  if (website) {
    // Deschide site-ul școlii într-un tab nou
    window.open(website, '_blank') 
    
    // Dacă preferi să se deschidă în același tab, folosește în schimb:
    // window.location.href = website 
  } else {
    // Fallback către pagina principală a aplicației dacă nu există link
    router.push('/')
  }
}
const roles = [
  { label: 'Profesor',  icon: ['fas', 'chalkboard-teacher'] },
  { label: 'Elev',      icon: ['fas', 'user-graduate'] },
  { label: 'Părinte',   icon: ['fas', 'people-roof'] },
  { label: 'Admin',     icon: ['fas', 'user-shield'] },
]

const toggleDarkMode = () => app.toggleDark()

// ─── Redirect după rol ────────────────────────────────────
const getRedirectPath = (userType) => {
  switch (userType) {
    case 'ADMIN':   return '/admin/dashboard'
    case 'TEACHER': return '/catalog/teacher/dashboard'
    case 'STUDENT': return '/catalog/student/dashboard'
    case 'PARENT':  return '/catalog/parent/dashboard'
    default:        return '/'
  }
}

// ─── Verifică dacă e deja logat ──────────────────────────
// ─── Verifică dacă e deja logat ──────────────────────────
const checkAuthAndRedirect = () => {
  // ✅ FIX: Verificăm 'refresh' token-ul, nu 'access', pentru că ăsta trăiește mai mult
  const refreshToken = localStorage.getItem('refresh')
  const userType = localStorage.getItem('user_type')
  
  if (!refreshToken || !userType) return false

  try {
    const decoded = jwtDecode(refreshToken)
    // Verificăm dacă a expirat refresh token-ul
    if (decoded.exp < Date.now() / 1000) {
      localStorage.removeItem('access')
      localStorage.removeItem('refresh')
      localStorage.removeItem('user_type')
      return false
    }
    
    // Dacă refresh-ul e încă valid, îl trimitem direct în dashboard
    router.replace(getRedirectPath(userType))
    return true
  } catch {
    localStorage.clear() // Curățăm tot dacă token-ul e corupt
    return false
  }
}
// ─── Login ───────────────────────────────────────────────
const login = async () => {
  if (!email.value || !password.value) {
    return Swal.fire({
      title: 'Atenție',
      text: 'Te rugăm să completezi toate câmpurile.',
      icon: 'warning',
      confirmButtonColor: 'var(--primary-color)',
      background: darkMode.value ? '#1f2937' : '#ffffff',
      color: darkMode.value ? '#e5e7eb' : '#111827',
    })
  }

  loading.value = true
  try {
    const { data } = await axios.post('/auth/login/', {
      email: email.value,
      password: password.value,
    })

    localStorage.setItem('access',      data.access)
    localStorage.setItem('refresh',     data.refresh)
    localStorage.setItem('user_type',   data.user_type)
    localStorage.setItem('school_name', data.school_name)
    localStorage.setItem('school_id',   data.school_id)
    localStorage.setItem('full_name',   data.full_name)

    await Swal.fire({
      title: 'Succes!',
      text: `Bine ai revenit, ${data.full_name || data.email}!`,
      icon: 'success',
      confirmButtonColor: 'var(--primary-color)',
      background: darkMode.value ? '#1f2937' : '#ffffff',
      color: darkMode.value ? '#e5e7eb' : '#111827',
      iconColor: 'var(--accent-color)',
      timer: 1200,
      showConfirmButton: false,
    })

    window.location.href = getRedirectPath(data.user_type)
  } catch (error) {
    Swal.fire({
      title: 'Eroare la autentificare',
      text: error.response?.data?.error || 'Email sau parolă incorecte. Verifică datele și încearcă din nou.',
      icon: 'error',
      confirmButtonColor: 'var(--primary-color)',
      background: darkMode.value ? '#1f2937' : '#ffffff',
      color: darkMode.value ? '#e5e7eb' : '#111827',
      iconColor: '#ef4444',
    })
  } finally {
    loading.value = false
  }
}

// ─── Preîncarcă date școală dacă lipsesc ─────────────────
const fetchSchoolDetails = async () => {
  if (!app.school?.name) {
    try {
      const { data } = await axios.get('/school/details/')
      app.setSchoolDetails(data)
      app.setCustomColors(data.primary_color, data.accent_color)
    } catch (err) {
      console.error('Eroare la încărcarea datelor școlii:', err)
    }
  }
}

const updateSEO = () => {
  const schoolName = app.school?.name || 'Portal Școlar'
  document.title = `Autentificare | ${schoolName}`

  const metaDesc = document.querySelector('meta[name="description"]')
  if (metaDesc) metaDesc.setAttribute('content', `Autentifică-te în catalogul digital al ${schoolName}. Acces pentru profesori, elevi, părinți și administratori.`)

  const ogTitle = document.querySelector('meta[property="og:title"]')
  if (ogTitle) ogTitle.setAttribute('content', `Autentificare | ${schoolName}`)
}

onMounted(async () => {
  if (checkAuthAndRedirect()) return
  await fetchSchoolDetails()
  updateSEO()
})
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500;600&display=swap');

/* PAGE */
.login-page { font-family:'DM Sans',sans-serif; min-height:100vh; display:flex; align-items:center; justify-content:center; position:relative; overflow:hidden; padding:40px 16px; }
.login-page--light { background:linear-gradient(135deg,#eef2ff 0%,#f8faff 60%,#fdf4ff 100%); }
.login-page--dark  { background:linear-gradient(135deg,#060911 0%,#090d1d 60%,#0c0914 100%); }
.hero-grid-overlay { position:absolute; inset:0; pointer-events:none; background-image:linear-gradient(rgba(99,102,241,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(99,102,241,.06) 1px,transparent 1px); background-size:48px 48px; }
.login-page--dark .hero-grid-overlay { background-image:linear-gradient(rgba(255,255,255,.025) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.025) 1px,transparent 1px); }
.orb { position:absolute; border-radius:50%; filter:blur(90px); pointer-events:none; animation:orb-float 10s ease-in-out infinite alternate; }
.orb-1 { width:500px; height:500px; top:-150px; right:-100px; background:radial-gradient(circle,color-mix(in srgb,var(--primary-color) 28%,transparent),transparent 70%); }
.orb-2 { width:300px; height:300px; bottom:-80px; left:40px; background:radial-gradient(circle,color-mix(in srgb,var(--accent-color) 20%,transparent),transparent 70%); animation-delay:-4s; }
@keyframes orb-float { from{transform:translate(0,0) scale(1)} to{transform:translate(20px,-28px) scale(1.06)} }

/* WRAP */
.login-wrap { width:100%; max-width:440px; }

/* HELP */
.help-text {
  margin: 16px 36px 0;
  text-align: center;
  font-size: 0.78rem;
  line-height: 1.4;
}

.help-link {
  color: var(--primary-color);
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.2s;
}

.help-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}
 
/* CARD */
.login-card { position:relative; overflow:hidden; border-radius:28px; border:1px solid; animation:card-in .5s cubic-bezier(.22,1,.36,1); }
.login-card--light { background:#fff; border-color:#e8edf5; box-shadow:0 20px 60px rgba(0,0,0,.1); }
.login-card--dark  { background:#111827; border-color:#1f2937; box-shadow:0 20px 60px rgba(0,0,0,.4); }
@keyframes card-in { from{opacity:0;transform:translateY(24px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
.card-stripe { position:absolute; top:0; left:0; right:0; height:4px; background:linear-gradient(90deg,var(--primary-color) 0%,var(--accent-color) 100%); }

/* HEADER */
.login-header { padding:36px 36px 0; text-align:center; display:flex; flex-direction:column; align-items:center; gap:12px; }
.logo-wrap { margin-bottom:4px; }
.logo-img { width:64px; height:64px; border-radius:50%; object-fit:cover; box-shadow:0 4px 16px rgba(0,0,0,.15); }
.logo-icon-bg { width:64px; height:64px; border-radius:20px; background:color-mix(in srgb,var(--primary-color) 12%,transparent); display:flex; align-items:center; justify-content:center; }
.logo-icon { font-size:1.8rem; color:var(--primary-color); }
.eyebrow-pill { display:inline-flex; align-items:center; gap:7px; padding:4px 12px; border-radius:999px; font-size:.7rem; font-weight:700; letter-spacing:.1em; text-transform:uppercase; }
.eyebrow-pill--light { background:color-mix(in srgb,var(--primary-color) 10%,transparent); border:1px solid color-mix(in srgb,var(--primary-color) 25%,transparent); color:var(--primary-color); }
.eyebrow-pill--dark  { background:rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.12); color:color-mix(in srgb,var(--primary-color) 80%,white); }
.eyebrow-dot { width:6px; height:6px; border-radius:50%; background:var(--primary-color); animation:dot-pulse 2s ease-in-out infinite; }
@keyframes dot-pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.6);opacity:.4} }
.login-title { font-family:'Playfair Display',serif; font-size:1.55rem; font-weight:900; letter-spacing:-.02em; line-height:1.2; margin:0; }
.login-subtitle { font-size:.85rem; margin:0; }

/* FORM */
.login-form { padding:28px 36px 20px; display:flex; flex-direction:column; gap:18px; }
.form-field { display:flex; flex-direction:column; gap:6px; }
.field-label { font-size:.75rem; font-weight:700; text-transform:uppercase; letter-spacing:.07em; }
.password-wrap { position:relative; }
.input-base { width:100%; padding:12px 16px; border-radius:12px; border:1.5px solid; font-size:.95rem; font-family:'DM Sans',sans-serif; transition:border-color .2s,box-shadow .2s,transform .2s; outline:none; box-sizing:border-box; }
.input-base--light { background:#f8faff; border-color:#e2e8f0; color:#111; }
.input-base--dark  { background:#1e293b; border-color:#334155; color:#f1f5f9; }
.input-base:focus  { border-color:var(--primary-color); box-shadow:0 0 0 3px color-mix(in srgb,var(--primary-color) 15%,transparent); transform:translateY(-1px); }
.password-wrap .input-base { padding-right:44px; }
.password-toggle { position:absolute; right:14px; top:50%; transform:translateY(-50%); background:none; border:none; cursor:pointer; padding:4px; transition:color .2s; }
.password-toggle:hover { color:var(--primary-color) !important; }

/* SUBMIT */
.btn-submit { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; padding:14px; border-radius:12px; background:var(--primary-color); color:#fff; font-size:.95rem; font-weight:700; border:none; cursor:pointer; font-family:'DM Sans',sans-serif; box-shadow:0 6px 20px -4px color-mix(in srgb,var(--primary-color) 55%,transparent); transition:transform .2s,box-shadow .2s,opacity .2s; }
.btn-submit:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 12px 28px -4px color-mix(in srgb,var(--primary-color) 60%,transparent); }
.btn-submit:disabled { opacity:.65; cursor:not-allowed; }
.btn-arrow { width:18px; height:18px; transition:transform .2s; }
.btn-submit:hover .btn-arrow { transform:translateX(4px); }

/* ROLE BADGES */
.role-section { margin:0 36px; padding:16px; border-radius:14px; border:1px solid; display:flex; flex-direction:column; gap:10px; }
.role-section--light { background:#f8faff; border-color:#e8edf5; }
.role-section--dark  { background:rgba(255,255,255,.03); border-color:#1f2937; }
.role-label { font-size:.68rem; font-weight:700; text-transform:uppercase; letter-spacing:.1em; opacity:.4; margin:0; }
.role-badges { display:flex; gap:8px; flex-wrap:wrap; }
.role-badge { display:inline-flex; align-items:center; gap:5px; padding:4px 10px; border-radius:999px; font-size:.72rem; font-weight:600; border:1px solid; }
.role-badge--light { background:color-mix(in srgb,var(--primary-color) 6%,transparent); border-color:color-mix(in srgb,var(--primary-color) 20%,transparent); color:#374151; }
.role-badge--dark  { background:rgba(255,255,255,.05); border-color:rgba(255,255,255,.1); color:#94a3b8; }

/* FOOTER */
.login-footer { padding:20px 36px 28px; display:flex; align-items:center; justify-content:space-between; }
.back-link { display:inline-flex; align-items:center; gap:7px; font-size:.82rem; font-weight:600; text-decoration:none; padding:7px 14px; border-radius:999px; border:1.5px solid; transition:all .2s; }
.back-link--light { border-color:color-mix(in srgb,var(--primary-color) 30%,transparent); color:var(--primary-color); }
.back-link--dark  { border-color:rgba(255,255,255,.15); color:color-mix(in srgb,var(--primary-color) 80%,white); }
.back-link:hover  { background:color-mix(in srgb,var(--primary-color) 8%,transparent); }

.dark-toggle { width:36px; height:36px; border-radius:10px; border:1.5px solid; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .2s; font-size:.9rem; background:transparent; }
.dark-toggle--light { border-color:#e2e8f0; color:#64748b; }
.dark-toggle--dark  { border-color:#334155; color:#94a3b8; }
.dark-toggle:hover { border-color:var(--primary-color); color:var(--primary-color); background:color-mix(in srgb,var(--primary-color) 8%,transparent); }
</style>